import React, { useContext, useEffect, useState } from "react";
import { AlertContext } from "@actinc/dls/context";
import { Plus as PlusIcon } from "@actinc/dls/icons";
import {
  AppBar,
  Box,
  Button,
  DialogContentText,
  Paper,
  Toolbar,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "urql";

import {
  AlertDialog,
  Backdrop,
  DataGrid,
  NoAccess,
} from "../../../../components";
import { ReviewGroupDialog } from "./ReviewGroupDialog";

import { disableAction, setTitle } from "../../../../services/utils";
import { ProgramsContext } from "../../../../programsProvider";

const MANAGE_REVIEW_GROUPS = "manageReviewGroups";

const AdminProgramReviewGroups = () => {
  const navigate = useNavigate();
  const { programID } = useParams();
  const programs = useContext(ProgramsContext);
  const program = programs.find((p) => p.id === programID);
  const alertContext = useContext(AlertContext);

  const [isLoading, setIsLoading] = useState(false);
  const [dialogState, setDialogState] = useState({ group: null, open: false });
  const [alertDialogProps, setAlertDialogProps] = useState({ open: false });

  const [getGroupsResponse, refetchGroups] = useQuery({
    query: getExternalReviewerGroupsQuery,
    variables: { program: programID },
  });

  const [, deleteGroup] = useMutation(deleteExternalReviewerGroupMutation);

  useEffect(() => {
    setTitle(["CDE", "Admin", "Programs", program?.name, "Review Groups"]);
  }, [program?.name]);

  if (!program?._actions?.includes(MANAGE_REVIEW_GROUPS)) {
    return (
      <NoAccess message="You do not have permission to manage review groups for this program." />
    );
  }

  const closeDialog = (changed) => {
    setDialogState({ group: null, open: false });
    if (changed) refetchGroups({ requestPolicy: "network-only" });
  };

  const openDialog = (group) => () => {
    setDialogState({ group: group || null, open: true });
  };

  const handleDeleteGroup = async (group) => {
    setIsLoading(true);
    const result = await deleteGroup({ id: group.id });
    setIsLoading(false);
    if (result.error) {
      console.error("Delete External Reviewer Group error:", result.error);
      alertContext.actions.addAlert({
        message: `Failed to delete review group "${group?.name}". Please try again.`,
        options: { variant: "error" },
      });
      return;
    }
    refetchGroups({ requestPolicy: "network-only" });
  };

  const openDeleteConfirm = (group) => () => {
    setAlertDialogProps({
      confirmButtonText: "Delete",
      dialogTitle: "Delete Review Group",
      message: (
        <DialogContentText aria-label="Delete Review Group Alert Text">
          {`Delete review group "${group?.name}"?`}
        </DialogContentText>
      ),
      onClose: (confirmed) => {
        setAlertDialogProps({ open: false });
        if (confirmed) handleDeleteGroup(group);
      },
      open: true,
    });
  };

  const columns = [
    {
      field: "name",
      flex: 1,
      headerName: "Group Name",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
    },
    {
      field: "createdBy",
      flex: 1,
      headerName: "Created By",
      type: "string",
      valueGetter: ({ row }) => row.createdBy?.name || "",
    },
    {
      field: "createdDateTime",
      headerName: "Created",
      type: "date",
      valueGetter: ({ row }) =>
        row.createdDateTime ? new Date(row.createdDateTime) : null,
      width: 180,
    },
    {
      field: "modifiedBy",
      flex: 1,
      headerName: "Modified By",
      type: "string",
      valueGetter: ({ row }) => row.modifiedBy?.name || "",
    },
    {
      field: "modifiedDateTime",
      headerName: "Modified",
      type: "date",
      valueGetter: ({ row }) =>
        row.modifiedDateTime ? new Date(row.modifiedDateTime) : null,
      width: 180,
    },
  ];

  const dataGridProps = {
    actions: [
      {
        disabled: disableAction(MANAGE_REVIEW_GROUPS),
        label: "Edit",
        onClick: (row) => openDialog(row),
      },
      {
        disabled: disableAction(MANAGE_REVIEW_GROUPS),
        label: "Delete",
        onClick: (row) => openDeleteConfirm(row),
      },
    ],
    columns,
    error: getGroupsResponse?.error?.message,
    loading: getGroupsResponse?.fetching,
    rows: getGroupsResponse?.data?.externalReviewerGroups || [],
    sortModel: [{ field: "name", sort: "asc" }],
  };

  return (
    <>
      <AppBar color="secondary" position="sticky" sx={{ zIndex: "drawer" }}>
        <Toolbar sx={{ justifyContent: "space-between" }} variant="dense">
          {program && (
            <Box sx={{ display: "flex" }}>
              <Typography sx={{ marginRight: 2 }} variant="subtitle1">
                Program:
              </Typography>
              <Typography id="programName" variant="subtitle1">
                {program.name}
              </Typography>
            </Box>
          )}
          <Button
            color="primary"
            onClick={() => navigate("/admin/programs")}
            size="small"
            variant="contained"
          >
            Close
          </Button>
        </Toolbar>
      </AppBar>
      <Backdrop loading={isLoading} />
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginX: 2,
            marginY: 1,
          }}
        >
          <Typography variant="h4">Manage Review Groups</Typography>
          <Button
            onClick={openDialog(null)}
            size="small"
            startIcon={<PlusIcon />}
            variant="outlined"
          >
            Create Group
          </Button>
        </Box>
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            marginX: 2,
            marginY: 1,
          }}
        >
          {alertDialogProps.open && <AlertDialog {...alertDialogProps} />}
          <Box sx={{ flexGrow: 1, minHeight: "500px" }}>
            <DataGrid key="reviewGroups" {...dataGridProps}></DataGrid>
          </Box>
        </Paper>
      </Box>
      {dialogState.open && (
        <ReviewGroupDialog
          closeDialog={closeDialog}
          existingGroups={getGroupsResponse?.data?.externalReviewerGroups || []}
          group={dialogState.group}
          programID={programID}
        />
      )}
    </>
  );
};

const getExternalReviewerGroupsQuery = `
  query GetExternalReviewerGroups($program: ID!) {
    externalReviewerGroups(program: $program) {
      id
      name
      reviewers
      createdBy { id name }
      createdDateTime
      modifiedBy { id name }
      modifiedDateTime
      _actions
    }
  }`;

const deleteExternalReviewerGroupMutation = `
  mutation DeleteExternalReviewerGroup($id: ID!) {
    deleteExternalReviewerGroup(id: $id)
  }`;

export {
  AdminProgramReviewGroups,
  deleteExternalReviewerGroupMutation,
  getExternalReviewerGroupsQuery,
};
